import React, { useState } from 'react';
import { useLanguage } from '../../../contexts/LanguageContext';
import { isSupportTicketsEnabled } from '../../../lib/support/supportContact';
import SupportContactCard from '../../../components/SupportContactCard/SupportContactCard';

interface FaqItem {
    question: string;
    answer: string;
}

const CONTACT_SUPPORT_QUESTION_TEXT = 'How do I contact support?';

const faqs: FaqItem[] = [
    { question: 'How do I add a new branch?', answer: 'Navigate to the Branches page from the sidebar and click "Add Branch". Fill in the required fields including name, address, and phone number.' },
    { question: 'How do I manage my appointments?', answer: 'Go to the Appointments page from the sidebar. You can view, confirm, reschedule, or cancel appointments from there.' },
    { question: 'How do I update my business hours?', answer: 'Go to Settings then Account Settings. Scroll down to the Business Hours section where you can toggle days and set opening/closing times.' },
    { question: 'How do I add services?', answer: 'Navigate to the Services page and click "Add Service". Fill in the service details including name, duration, price, and category.' },
    { question: CONTACT_SUPPORT_QUESTION_TEXT, answer: 'Go to the Support page from the sidebar and click "New Ticket" to submit a support request. Our team will respond within 24 hours.' },
    { question: 'How do I change my password?', answer: 'Go to Settings then Profile, scroll down, and click "Change password". Enter your current password and then your new password.' },
];

const Help: React.FC = () => {
    const { t } = useLanguage();
    const [expandedIndex, setExpandedIndex] = useState<number | null>(null);
    // AB#2133 — tickets proxy to Zammad (OFF for the pilot): while the flag is
    // off, point owners at the Support page's contact card, not at a ticket.
    const ticketsEnabled = isSupportTicketsEnabled();
    const answerFor = (faq: FaqItem): string =>
        !ticketsEnabled && faq.question === CONTACT_SUPPORT_QUESTION_TEXT
            ? t('help.faq.contactSupport.pilotAnswer')
            : faq.answer;

    const toggleFaq = (index: number) => {
        setExpandedIndex(prev => (prev === index ? null : index));
    };

    return (
        <div style={{ padding: '20px', maxWidth: 800 }}>
            <h1 className='xH1'>Help</h1>

            <div style={{ marginTop: 24 }}>
                <h2 style={{ fontSize: '1.2rem', marginBottom: 16 }}>Frequently Asked Questions</h2>
                {faqs.map((faq, index) => (
                    <div
                        key={index}
                        style={{
                            border: '1px solid var(--border-subtle)',
                            borderRadius: 8,
                            marginBottom: 8,
                            overflow: 'hidden',
                        }}
                    >
                        <button
                            onClick={() => toggleFaq(index)}
                            style={{
                                display: 'flex',
                                justifyContent: 'space-between',
                                alignItems: 'center',
                                width: '100%',
                                padding: '14px 16px',
                                border: 'none',
                                background: 'none',
                                cursor: 'pointer',
                                fontSize: '0.95rem',
                                fontWeight: 600,
                                textAlign: 'left',
                            }}
                        >
                            {faq.question}
                            <span style={{ fontSize: '1.2rem', marginLeft: 8 }}>
                                {expandedIndex === index ? '\u2212' : '+'}
                            </span>
                        </button>
                        {expandedIndex === index && (
                            <div style={{ padding: '0 16px 14px', color: 'var(--text-secondary)', fontSize: '0.9rem', lineHeight: 1.5 }}>
                                {answerFor(faq)}
                            </div>
                        )}
                    </div>
                ))}
            </div>

            <div style={{ marginTop: 32 }}>
                <h2 style={{ fontSize: '1.2rem', marginBottom: 12 }}>Need more help?</h2>
                {ticketsEnabled ? (
                    <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5 }}>
                        If you cannot find the answer you are looking for, please visit the Support page to submit a ticket. Our team is available to assist you.
                    </p>
                ) : (
                    <>
                        {/* SO-36 — the contact itself, right here: Help no longer sends
                            the owner to the Support page just to find it (dead-end loop). */}
                        <p style={{ color: 'var(--text-secondary)', lineHeight: 1.5, marginBottom: 12 }}>
                            {t('help.needMoreHelp.pilotBody')}
                        </p>
                        <SupportContactCard />
                    </>
                )}
            </div>
        </div>
    );
};

export default Help;
